/**
 * Design tokens — colour, typography, spacing and interaction-state values.
 *
 * ADR-20260821-84e5: this is the ONE module declaring these values. No
 * second, library-neutral mapping module exists anywhere in the repo — if
 * you are tempted to add one, add the value here instead and read it from
 * `theme.ts`.
 *
 * ADR-20260917-m51n / the 2026-09-17 product-spec reword: these are
 * Aoife's real, final FRP values (`docs/ux-review/design_handoff_renewals_portal/DESIGN-TOKENS.md`),
 * shipped now — not Material UI defaults with a later swap. `theme.ts`
 * reads only this module to build the Material UI theme (AC-4): replacing
 * every value below changes no file outside this one.
 *
 * Font FILES are not this module's concern — TG-3 self-hosts
 * Calibri/Carlito/Segoe UI under `public/fonts`. This module only declares
 * the family stack.
 */

export const tokens = {
  colour: {
    brand: {
      50: "#eaf2f6",
      100: "#92b9c9",
      300: "#6aa1b8",
      500: "#3d5e80",
      700: "#0f3253",
      900: "#0a2340",
    },
    accent: {
      50: "#fff6e0",
      300: "#fbd476",
      500: "#fdb51e",
    },
    neutral: {
      white: "#ffffff",
      surface: "#f4f7f9",
      borderSubtle: "#d6dfe6",
      borderControl: "#628ca3",
      borderDisabled: "#a9bcc9",
      ink: "#1c2b3a",
      inkMuted: "#54677a",
      inkDisabled: "#7d8fa0",
      // ADR-20261016-v6nd (AC-2's "section-list card and timeline"): H2's
      // own section-list row connector colour
      // (`04-05-GuidedExposures.dc.html`'s own `connector` style), one of
      // the H2-specific literals `DESIGN-SYSTEM-DEPARTURES.md`'s "Other
      // known departures" records (e.g. the Screen 3b underlined button's
      // `#0f3253`) -- not part of the published design system's own
      // palette, but still declared here, never inline, per
      // ADR-20260821-84e5 ("tokens.ts is the ONE module declaring these
      // values") and this repo's `no-colour-literal-outside-tokens`
      // ESLint rule.
      timelineConnector: "#b4c2cc",
    },
    // ADR-20261102-h2fd: colours H2 draws that are not in its token set -- the dialog scrim, and
    // the browser's own button text colours its stepper and help triggers inherit.
    h2: {
      dialogScrim: "rgba(15, 50, 83, 0.45)",
      buttonText: "rgb(0, 0, 0)",
      buttonTextDisabled: "rgba(16, 16, 16, 0.3)",
    },
    status: {
      error: { text: "#a4262c", surface: "#fdeced" },
      success: { text: "#1a6b41", surface: "#e8f4ee" },
      warning: { text: "#7a4d00", surface: "#fff6e0" },
      info: { text: "#3d5e80", surface: "#eaf2f6" },
    },
    focus: {
      onLight: "#0f3253",
      onDark: "#fdb51e",
    },
  },

  typography: {
    // Font FILES are TG-3's concern (self-hosted, no third-party origin).
    // This is only the family stack value.
    fontFamily: '"Calibri", "Carlito", "Segoe UI", system-ui, sans-serif',
    weights: { regular: 400, bold: 700 },
    // Only these ten semantic variants exist. There is no "medium" weight —
    // asking for one produces a synthesised face that renders badly, so the
    // adapter's Typography prop type has no way to request one.
    variants: {
      display: { size: "2.25rem", mobileSize: "1.75rem", weight: 700, lineHeight: 1.15, letterSpacing: "-0.01em" },
      heading1: { size: "1.75rem", mobileSize: "1.5rem", weight: 700, lineHeight: 1.2, letterSpacing: "-0.01em" },
      heading2: { size: "1.375rem", mobileSize: "1.25rem", weight: 700, lineHeight: 1.25, letterSpacing: "-0.005em" },
      heading3: { size: "1.125rem", mobileSize: "1.0625rem", weight: 700, lineHeight: 1.3, letterSpacing: "0" },
      eyebrow: { size: "0.875rem", mobileSize: "0.875rem", weight: 700, lineHeight: 1.3, letterSpacing: "0.06em" },
      bodyLarge: { size: "1.1875rem", mobileSize: "1.125rem", weight: 400, lineHeight: 1.55, letterSpacing: "0" },
      body: { size: "1.0625rem", mobileSize: "1.0625rem", weight: 400, lineHeight: 1.6, letterSpacing: "0" },
      bodySmall: { size: "0.9375rem", mobileSize: "0.9375rem", weight: 400, lineHeight: 1.55, letterSpacing: "0" },
      helper: { size: "0.875rem", mobileSize: "0.875rem", weight: 400, lineHeight: 1.45, letterSpacing: "0" },
      action: { size: "1.0625rem", mobileSize: "1.0625rem", weight: 700, lineHeight: 1.2, letterSpacing: "0" },
    },
  },

  // 4px base scale — theme.ts sets Material UI's spacing multiplier to 4
  // so `theme.spacing(n)` and this table agree (spacing(1) === xxs, etc.).
  spacing: {
    none: 0,
    xxs: 4,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    xxxl: 64,
  },

  interactionState: {
    border: {
      hairlineWidth: 1,
      controlWidth: 1,
      emphasisWidth: 2,
      focusWidth: 3,
      subtle: "#d6dfe6",
      control: "#628ca3",
      disabled: "#a9bcc9",
    },
    focusRing: {
      width: 3,
      offset: 2,
      colourOnLight: "#0f3253",
      colourOnDark: "#fdb51e",
    },
    radius: {
      none: 0,
      control: 4,
      surface: 8,
      pill: 999,
    },
    elevation: {
      raised: "0 1px 2px rgba(15, 50, 83, 0.08)",
      card: "0 2px 8px rgba(15, 50, 83, 0.10)",
      overlay: "0 8px 24px rgba(15, 50, 83, 0.12)",
    },
    targetSize: {
      minimum: 24,
      standard: 44,
      touch: 48,
    },
    motion: {
      duration: { fast: 120, base: 200, slow: 320 },
      easing: "cubic-bezier(0.2, 0, 0, 1)",
    },
  },

  layout: {
    containerMax: 1200,
    // ADR-20261016-v6nd (AC-2's "760px content width"): H2's own departure
    // record (`DESIGN-SYSTEM-DEPARTURES.md`, "Content width") is explicit
    // that the portal's real content width is 760px, not the published
    // design system's own 720px default -- this was 720 (the design
    // system default, never actually applied anywhere until the same fix
    // wired it into `Container.tsx`) until this fix. Equal to
    // `footerContentMax` below by design: H2 uses the same 760px measure
    // for both.
    contentMax: 760,
    footerContentMax: 760,
    gutters: { xs: 16, sm: 24, md: 32 },
    minViewport: 320,
    breakpoints: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
} as const;

export type Tokens = typeof tokens;
export type TypographyVariantName = keyof Tokens["typography"]["variants"];
export type SpacingToken = keyof Tokens["spacing"];
